import { SetStateAction } from "react";
import { useDiabolo } from "@/providers/diabolocom";
import { DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { PhoneOutgoing, Mail } from "lucide-react";
import { Appointments } from "@/components/apprenticeship/candidate/appointments";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";
import { PaginatedResponse } from "@/types/common";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFetcher } from "react-router-dom";
import { FormError } from "@/components/ui/form-error";
import { Textarea } from "@/components/ui/textarea";
import { NavigationStatus } from "@/components/utils/navigation-status";
import { TaskType } from "@/types/user";
import { DataService } from "@/services/data";
import { toast } from "sonner";

export const DialogInner = ({ task, isDialogOpen, setIsDialogOpen }: { task: TaskType; isDialogOpen: boolean; setIsDialogOpen: React.Dispatch<SetStateAction<boolean>> }) => {
    const [isLoading, setIsloading] = useState<boolean>(false);
    const [appointments, setAppointments] = useState<PaginatedResponse<ApprenticeshipAppointmentType>>();
    const { dialNumber } = useDiabolo();
    const [taskType, setTaskType] = useState<string>("student");
    const fetcher = useFetcher();

    const formSchema = z.object({
        content: z.string().optional()
    });

    const form = useForm<z.infer<typeof formSchema>>({
        mode: "all",
        resolver: zodResolver(formSchema),
        defaultValues: {
            content: ""
        }
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        if (task.reference) {
            if (taskType === "student" && task.reference.id) {
                fetcher.submit(
                    {
                        contact: { id: task.reference.id },
                        content: values.content || null
                    },
                    { method: "post", action: `/alternance/tasks/${task.id}`, encType: "application/json" }
                );
            }
            if (taskType === "company" && task.reference.company_id) {
                fetcher.submit(
                    {
                        registration: { id: task.reference.id },
                        company: { id: task.reference.company_id },
                        content: values.content || null
                    },
                    { method: "post", action: `/alternance/tasks`, encType: "application/json" }
                );
            }
        }
    };

    useEffect(() => {
        if (fetcher.data) {
            if (fetcher.data.success) {
                toast.success("La note a bien été créée");
                setIsDialogOpen(false);
            }
        }
    }, [fetcher.data]);

    useEffect(() => {
        if (isDialogOpen) {
            if (task.reference_type === "apprenticeship:registration" && task.reference && task.type === "meeting") {
                setIsloading(true);
                DataService.client
                    .url(`/apprenticeship/appointments/?student_id=${task.reference?.id}&page_size=1000`)
                    .get()
                    .json()
                    .then(resp => {
                        setAppointments(resp as PaginatedResponse<ApprenticeshipAppointmentType>);
                    })
                    .catch(console.error)
                    .finally(() => {
                        setIsloading(false);
                    });
            }
        }
    }, [isDialogOpen]);

    switch (task.type) {
        case "call":
            return (
                <>
                    <DialogHeader>
                        <DialogTitle className="flex flex-row">{task.title}</DialogTitle>
                        <DialogDescription>Choisissez la personne à appeler</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-4 py-4">
                        <div className="flex flex-row items-center">
                            <p className="mr-2">
                                {task.reference?.first_name} {task.reference?.last_name} (Elève)
                            </p>
                            <PhoneOutgoing
                                size={16}
                                className="inline cursor-pointer"
                                onClick={() => {
                                    if (task.reference?.mobile_phone) {
                                        dialNumber(task.reference?.mobile_phone);
                                        setIsDialogOpen(false);
                                    }
                                }}
                            />
                        </div>
                        {task.reference?.cerfa_tutor_mobile_phone && (
                            <div className="flex flex-row items-center">
                                <p className="mr-2">
                                    {task.reference?.cerfa_tutor_first_name} {task.reference?.cerfa_tutor_last_name} (Tuteur)
                                </p>
                                <PhoneOutgoing
                                    size={16}
                                    className="inline cursor-pointer"
                                    onClick={() => {
                                        if (task.reference?.cerfa_tutor_mobile_phone) {
                                            dialNumber(task.reference?.cerfa_tutor_mobile_phone);
                                            setIsDialogOpen(false);
                                        }
                                    }}
                                />
                            </div>
                        )}
                        {task.reference?.apprenticeship_company_responsable_mobile_phone && (
                            <div className="flex flex-row items-center">
                                <p className="mr-2">
                                    {task.reference?.apprenticeship_company_responsable_first_name} {task.reference?.apprenticeship_company_responsable_last_name} (Responsable entreprise)
                                </p>
                                <PhoneOutgoing
                                    size={16}
                                    className="inline cursor-pointer"
                                    onClick={() => {
                                        if (task.reference?.apprenticeship_company_responsable_mobile_phone) {
                                            dialNumber(task.reference?.apprenticeship_company_responsable_mobile_phone);
                                            setIsDialogOpen(false);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </>
            );
        case "meeting":
            return (
                <>
                    <DialogHeader>
                        <DialogTitle className="flex flex-row">{task.title}</DialogTitle>
                        <DialogDescription>{isLoading ? "Chargement..." : "Choisissez le meeting auquel participer"}</DialogDescription>
                    </DialogHeader>
                    <div>
                        {!isLoading && task.reference_type === "apprenticeship:registration" && task.reference && (
                            <Appointments registration={task.reference} appointments={appointments} type="dialog" />
                        )}
                    </div>
                </>
            );
        case "email":
            return (
                <>
                    <DialogHeader>
                        <DialogTitle className="flex flex-row">{task.title}</DialogTitle>
                        <DialogDescription>Choisissez la personne à qui envoyer un email</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-4 py-4">
                        <div className="flex flex-row items-center">
                            <p className="mr-2">
                                {task.reference?.first_name} {task.reference?.last_name} (Elève)
                            </p>
                            <Mail
                                size={16}
                                className="inline cursor-pointer"
                                onClick={() => {
                                    if (task.reference?.email) {
                                        window.location.href = `mailto:${task.reference?.email}?subject=${task.title}!`;
                                        setIsDialogOpen(false);
                                    }
                                }}
                            />
                        </div>
                        {task.reference?.cerfa_tutor_email && (
                            <div className="flex flex-row items-center">
                                <p className="mr-2">
                                    {task.reference?.cerfa_tutor_first_name} {task.reference?.cerfa_tutor_last_name} (Tuteur)
                                </p>
                                <Mail
                                    size={16}
                                    className="inline cursor-pointer"
                                    onClick={() => {
                                        if (task.reference?.cerfa_tutor_email) {
                                            window.location.href = `mailto:${task.reference?.cerfa_tutor_email}?subject=${task.title}!`;
                                            setIsDialogOpen(false);
                                        }
                                    }}
                                />
                            </div>
                        )}
                        {task.reference?.apprenticeship_company_responsable_email && (
                            <div className="flex flex-row items-center">
                                <p className="mr-2">
                                    {task.reference?.apprenticeship_company_responsable_first_name} {task.reference?.apprenticeship_company_responsable_last_name} (Responsable entreprise)
                                </p>
                                <Mail
                                    size={16}
                                    className="inline cursor-pointer"
                                    onClick={() => {
                                        if (task.reference?.apprenticeship_company_responsable_email) {
                                            window.location.href = `mailto:${task.reference?.apprenticeship_company_responsable_email}?subject=${task.title}!`;
                                            setIsDialogOpen(false);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </>
            );
        case "note":
            return (
                <>
                    <DialogHeader>
                        <DialogTitle className="flex flex-row">{task.title}</DialogTitle>
                        <DialogDescription>{isLoading ? "Chargement..." : "Ajouter une note"}</DialogDescription>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 p-4 outline-none">
                            <Tabs defaultValue="student" value={taskType} onValueChange={setTaskType} className="space-y-5">
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="student">Créer une note étudiant</TabsTrigger>
                                    <TabsTrigger value="company">Créer une note entreprise</TabsTrigger>
                                </TabsList>
                                <FormError errorMessage={fetcher.data?.message} className="mb-4" />
                                <TabsContent value="student">
                                    <FormField
                                        control={form.control}
                                        name="content"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Textarea rows={7} placeholder="Tout ce qui vous semble utile" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </TabsContent>
                                <TabsContent value="company" className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="content"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Textarea rows={7} placeholder="Tout ce qui vous semble utile" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </TabsContent>
                            </Tabs>

                            <DialogFooter>
                                <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={form.formState.isLoading || fetcher.state != "idle"}>
                                    <NavigationStatus fetcher={fetcher}>Enregistrer</NavigationStatus>
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </>
            );
        default:
            return <div>Erreur: Type de tache inconnue</div>;
    }
};
